import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { supabase } from '@/integrations/supabase/client';
import { snapshotWordingParts } from '@/lib/approvals/snapshot';
import {
  type ApprovalCandidate,
  type CreateApprovalRequestInput,
  approvalService,
} from '@/services/data/ApprovalService';

/**
 * Operator-side reads. These go through the normal authenticated Supabase
 * client so RLS evaluates — the service-role path exists only on the reviewer's
 * side, where there is no session.
 */
export const approvalKeys = {
  all: ['approvals'] as const,
  list: (workspaceId: string | null | undefined) => ['approvals', 'list', workspaceId] as const,
  detail: (requestId: string | null | undefined) => ['approvals', 'detail', requestId] as const,
  decisions: (workspaceId: string | null | undefined) =>
    ['approvals', 'decisions', workspaceId] as const,
  candidates: (workspaceId: string | null | undefined, eventId: string | null | undefined) =>
    ['approvals', 'candidates', workspaceId, eventId] as const,
  staleArticles: (workspaceId: string | null | undefined) =>
    ['approvals', 'stale-articles', workspaceId] as const,
};

/**
 * How many shared items are showing a topic blurb where a finished article now
 * exists. Drives the operator's banner on `/publishing/client-desk`.
 *
 * 🔴 THIS HOOK IS THE REASON THE SERVICE METHOD IS NOT DEAD CODE.
 * `ApprovalService.listChangedItems` was built to detect exactly this class of
 * drift and shipped with NO CALLER anywhere in `src/` — so 24 items sat wrong
 * on a real client's desk while the detector that would have found them was
 * present, correct and unreachable. Existence is not completion. If you ever
 * remove the banner, remove the service method too, or wire it somewhere else
 * in the same change.
 */
export function useStaleArticleCount(workspaceId: string | null | undefined) {
  return useQuery({
    queryKey: approvalKeys.staleArticles(workspaceId),
    enabled: Boolean(workspaceId),
    queryFn: () => approvalService.countStaleArticleItems(workspaceId as string),
    staleTime: 30_000,
  });
}

export function useRefreshStaleArticles() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (workspaceId: string) => approvalService.refreshStaleArticleItems(workspaceId),
    onSuccess: () => {
      // The desk payload and the banner both change. `desk-workspace` is the
      // key `useWorkspaceDesk` actually uses — invalidating a guessed `['desk']`
      // would refetch nothing and leave the operator looking at the old blurbs.
      void queryClient.invalidateQueries({ queryKey: approvalKeys.all });
      void queryClient.invalidateQueries({ queryKey: ['desk-workspace'] });
    },
  });
}

export function useApprovalRequests(workspaceId: string | null | undefined) {
  return useQuery({
    queryKey: approvalKeys.list(workspaceId),
    enabled: Boolean(workspaceId),
    queryFn: () => approvalService.listRequests(workspaceId as string),
    staleTime: 30_000,
    meta: { persist: false },
  });
}

/**
 * What the people in this workspace's room have actually said.
 *
 * Workspace-scoped rather than request-scoped on purpose — see
 * `ApprovalService.listDecisions`. A room member's decisions are invisible to
 * any read keyed on `request_id`.
 */
export function useApprovalDecisions(workspaceId: string | null | undefined) {
  return useQuery({
    queryKey: approvalKeys.decisions(workspaceId),
    enabled: Boolean(workspaceId),
    queryFn: () => approvalService.listDecisions(workspaceId as string),
    staleTime: 30_000,
  });
}

export function useApprovalRequest(requestId: string | null | undefined) {
  return useQuery({
    queryKey: approvalKeys.detail(requestId),
    enabled: Boolean(requestId),
    queryFn: () => approvalService.getRequest(requestId as string),
    staleTime: 15_000,
  });
}

export function useApprovalCandidates(
  workspaceId: string | null | undefined,
  reachEventId: string | null | undefined,
) {
  return useQuery({
    queryKey: approvalKeys.candidates(workspaceId, reachEventId),
    enabled: Boolean(workspaceId && reachEventId),
    queryFn: () =>
      approvalService.listCandidatesForEvent(workspaceId as string, reachEventId as string),
    staleTime: 15_000,
  });
}

/**
 * Candidates for an explicit set of tasks (the editorial programme send). Same
 * builder as the event path, so a re-send carries what the first send carried.
 */
export function useTaskCandidates(
  workspaceId: string | null | undefined,
  taskIds: string[],
  enabled = true,
) {
  return useQuery({
    queryKey: ['approvals', 'task-candidates', workspaceId, taskIds.join(',')] as const,
    enabled: Boolean(workspaceId) && enabled && taskIds.length > 0,
    queryFn: () => approvalService.listCandidatesForTasks(taskIds),
    staleTime: 0,
    gcTime: 0,
    meta: { persist: false },
  });
}

/**
 * Selected candidates whose last client decision was "changes requested" and
 * whose wording has not changed since. Drives the send dialogs' warning.
 */
export function useUnchangedResends(
  workspaceId: string | null | undefined,
  candidates: ApprovalCandidate[],
) {
  const signature = candidates
    .map(
      (candidate) =>
        `${candidate.projectTaskId ?? ''}:${snapshotWordingParts(
          candidate.title,
          candidate.snapshot as Record<string, unknown>,
        )
          .map((part) => part.length)
          .join('.')}`,
    )
    .join(',');
  return useQuery({
    queryKey: ['approvals', 'unchanged-resends', workspaceId, signature] as const,
    enabled: Boolean(workspaceId) && candidates.some((candidate) => candidate.projectTaskId),
    queryFn: () => approvalService.findUnchangedResends(workspaceId as string, candidates),
    staleTime: 0,
    meta: { persist: false },
  });
}

export function useDraftCandidates(workspaceId: string | null | undefined) {
  return useQuery({
    queryKey: ['approvals', 'draft-candidates', workspaceId] as const,
    enabled: Boolean(workspaceId),
    queryFn: () => approvalService.listDraftCandidates(workspaceId as string),
    staleTime: 30_000,
  });
}

export function useClientPhotoUrl(mediaId: string | null | undefined) {
  return useQuery({
    queryKey: ['approvals', 'photo', mediaId] as const,
    enabled: Boolean(mediaId),
    queryFn: () => approvalService.getClientPhotoUrl(mediaId as string),
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateApprovalRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateApprovalRequestInput) => approvalService.createRequest(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: approvalKeys.all });
    },
  });
}

export function useRevokeReviewer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (reviewerId: string) => approvalService.revokeReviewer(reviewerId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: approvalKeys.all });
    },
  });
}

export function useReissueApprovalLink() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (reviewerId: string) => approvalService.reissueLink(reviewerId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: approvalKeys.all });
    },
  });
}

export function useCloseApprovalRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (requestId: string) => approvalService.closeRequest(requestId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: approvalKeys.all });
    },
  });
}

export interface GeneratedVariation {
  label: string;
  body: string;
}

export interface VariationsResult {
  variations: GeneratedVariation[];
  /** False when the workspace has no brand voice configured — say so, never imply one. */
  groundedInBrandVoice: boolean;
  /** Angles the slop gate refused. Surfaced so a thin result is explained, not mysterious. */
  rejectedForSlop: string[];
}

/**
 * Alternate versions of one caption, in the workspace's own brand voice.
 *
 * Deliberately an OPERATOR-side action. The reviewer never generates: making
 * the client the author produces copy nobody reviewed and inverts the working
 * relationship. What the client gets is a choice the operator decided to offer.
 */
export function useGenerateVariations() {
  return useMutation({
    mutationFn: async (input: {
      text: string;
      platform?: string | null;
      brandKitId?: string | null;
      projectId?: string | null;
      count?: number;
    }): Promise<VariationsResult> => {
      // The route is `requireUser`-gated, and a bare fetch from the browser
      // carries no Supabase session — it 401s. Every other client->server call
      // in this repo attaches the bearer explicitly (caption-suggest.ts,
      // caption-rewrite.ts, selection-action.ts); this reads it at call time
      // rather than threading it through props.
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;
      if (!accessToken) throw new Error('You need to be signed in to generate options.');

      const response = await fetch('/api/content/variations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          text: input.text,
          platform: input.platform ?? undefined,
          brandKitId: input.brandKitId ?? undefined,
          projectId: input.projectId ?? undefined,
          count: input.count ?? 2,
        }),
      });
      if (!response.ok) {
        const body = (await response.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? 'Could not generate options.');
      }
      return (await response.json()) as VariationsResult;
    },
  });
}
